/**
 * =============================================================================
 * WORKFLOW STACK - THE WORKFLOW CANVAS / SEQUENCE PANEL
 * =============================================================================
 * 
 * PURPOSE:
 * This component renders the MAIN CANVAS where workflow steps are displayed
 * in sequence. It provides the SortableContext for @dnd-kit reordering.
 * 
 * DRAG-AND-DROP ARCHITECTURE:
 * - DndContext is provided by AutomationBuilder (parent) to enable unified
 *   drag-and-drop between ToolsBar and WorkflowStack
 * - This component provides SortableContext for the workflow nodes
 * - Drag end handling is done in AutomationBuilder
 * 
 * STEP ORDERING:
 * - Steps are rendered in array index order
 * - workflowSteps[0] is always the trigger (fixed, cannot be moved)
 * - workflowSteps[n] is last
 * 
 * VISUAL DISPLACEMENT (LIVE NODE MOVEMENT):
 * - Visual-only. Does not affect drop logic.
 * - @dnd-kit's SortableContext + useSortable automatically handle displacement.
 * - When dragging, other nodes smoothly shift to show where the drop will occur.
 * 
 * PLACEHOLDER FOR NEW NODES:
 * - Visual-only. Does not affect drop logic.
 * - When dragging a NEW tool from the sidebar, a temporary placeholder is
 *   inserted into the list so existing nodes can shift around it.
 * - The placeholder is NEVER persisted or passed to addNode().
 * - This enables live preview when adding new nodes, not just reordering.
 * =============================================================================
 */
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useDroppable, useDndMonitor } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import WorkflowNode from './WorkflowNode';
import { DRAG_TYPES } from '../constants/dragTypes';

/**
 * =============================================================================
 * PLACEHOLDER CONSTANT - Visual-only temporary node ID
 * =============================================================================
 * Visual-only. Does not affect drop logic.
 * This ID is used for the temporary placeholder node that appears when
 * dragging a new tool from the sidebar. It must NEVER be persisted.
 * =============================================================================
 */
const PLACEHOLDER_ID = '__placeholder__';

/**
 * WorkflowStack Component - The workflow canvas with sortable nodes
 * 
 * NOTE: DndContext is provided by AutomationBuilder to enable cross-component
 * drag-and-drop (ToolsBar → WorkflowStack). This component only needs
 * SortableContext for the workflow nodes.
 * 
 * VISUAL DISPLACEMENT:
 * - @dnd-kit handles all visual displacement automatically via SortableContext.
 * - WorkflowNode uses useSortable() which provides transform/transition styles.
 * - When dragging, non-dragged nodes shift to preview the drop position.
 * - This is purely visual - actual reorder happens on drop in AutomationBuilder.
 * 
 * PLACEHOLDER-DRIVEN INSERTION:
 * - When dragging a NEW tool, placeholderIndex is the SINGLE SOURCE OF TRUTH.
 * - On drop, this component calls onAddStep(type, placeholderIndex) directly.
 * - This ensures visual preview and final position are ALWAYS aligned.
 * 
 * @param {Object} props
 * @param {Array} props.workflowSteps - Array of step objects to render
 * @param {string|null} props.selectedNode - ID of currently selected step
 * @param {Function} props.onNodeClick - Callback when a step is clicked
 * @param {Function} props.onAddStep - Callback to add a new step: (type, index) => void
 * @param {Array} props.forms - Available forms (for trigger node display)
 * @param {string} props.automationStatus - Status of automation: DRAFT, ACTIVE, PAUSED
 * @param {Function} props.onStartAutomation - Callback when Start Automation is clicked
 * @param {boolean} props.isMobile - Whether to render mobile layout
 */
const WorkflowStack = ({
  workflowSteps,
  selectedNode,
  onNodeClick,
  onAddStep,
  forms = [],
  automationStatus = 'DRAFT',
  onStartAutomation,
  onStopAutomation,
  onTestRun,
  onRemoveStep,
  isMobile,
  isReadOnly = false
}) => {
  // =============================================================================
  // PLACEHOLDER STATE - Visual-only for new node preview
  // =============================================================================
  // Visual-only. Does not affect drop logic.
  // 
  // When dragging a NEW tool from the sidebar (not reordering existing nodes),
  // we insert a temporary placeholder into the list. This allows @dnd-kit's
  // SortableContext to compute displacement transforms for existing nodes,
  // creating the "live preview" effect where nodes shift to make room.
  // 
  // - placeholderIndex: Where to insert the placeholder (null = no placeholder)
  // - isDraggingNewTool: True when dragging from sidebar (DRAG_TYPES.TOOL)
  // 
  // The placeholder is NEVER persisted or passed to addNode().
  // =============================================================================
  const [placeholderIndex, setPlaceholderIndex] = useState(null);
  const [isDraggingNewTool, setIsDraggingNewTool] = useState(false);
  const nodeListRef = useRef(null);

  // =============================================================================
  // COMPUTE PLACEHOLDER INDEX - Visual-only calculation
  // =============================================================================
  // Visual-only. Does not affect drop logic.
  // Calculates where the placeholder should appear based on cursor Y position.
  // This determines where nodes will shift to make room.
  // =============================================================================
  const computePlaceholderIndex = useCallback((clientY) => {
    if (!nodeListRef.current) return workflowSteps.length;

    const nodeElements = nodeListRef.current.querySelectorAll('.stack-node');
    if (!nodeElements.length) return workflowSteps.length;

    // Find which gap the cursor is closest to
    for (let i = 0; i < nodeElements.length; i++) {
      const rect = nodeElements[i].getBoundingClientRect();
      const midPoint = rect.top + rect.height / 2;

      // Skip index 0 - can't insert before trigger
      if (i === 0) continue;

      // If cursor is above the midpoint of this node, placeholder goes before it
      if (clientY < midPoint) {
        return i;
      }
    }

    // Cursor is below all nodes - placeholder at end
    return workflowSteps.length;
  }, [workflowSteps.length]);

  // =============================================================================
  // DRAG STATE REF - Capture values for use in onDragEnd
  // =============================================================================
  // We need refs to capture the current values at drag end time,
  // because the event handler closure may have stale state values.
  // =============================================================================
  const placeholderIndexRef = useRef(null);
  const isDraggingNewToolRef = useRef(false);
  const draggedStepTypeRef = useRef(null);

  // Keep refs in sync with state
  placeholderIndexRef.current = placeholderIndex;
  isDraggingNewToolRef.current = isDraggingNewTool;

  // =============================================================================
  // DND MONITOR - Track drag events for placeholder management
  // =============================================================================
  // PLACEHOLDER-DRIVEN INSERTION:
  // - placeholderIndex is the SINGLE SOURCE OF TRUTH for insertion position.
  // - On drop, we call onAddStep(type, placeholderIndex) directly.
  // - This ensures the visual preview ALWAYS matches the final position.
  // - AutomationBuilder's handleDragEnd will skip TOOL handling (we do it here).
  // =============================================================================
  useDndMonitor({
    onDragStart(event) {
      // Skip placeholder logic in read-only mode
      if (isReadOnly) return;

      // Check if dragging a new tool from sidebar (not reordering existing)
      const dragType = event.active?.data?.current?.type;
      if (dragType === DRAG_TYPES.TOOL) {
        setIsDraggingNewTool(true);
        // Initial placeholder at end of list
        setPlaceholderIndex(workflowSteps.length);
        // Capture the step type for use on drop
        draggedStepTypeRef.current = event.active?.data?.current?.stepType;
      }
    },
    onDragMove(event) {
      // Skip in read-only mode
      if (isReadOnly) return;

      // Only track placeholder for new tool drags
      if (!isDraggingNewToolRef.current) return;

      // Get current pointer position
      // Use delta from initial position for accurate tracking
      const initialY = event.activatorEvent?.clientY ?? 0;
      const deltaY = event.delta?.y ?? 0;
      const pointerY = initialY + deltaY;

      // Update placeholder position
      const newIndex = computePlaceholderIndex(pointerY);
      if (newIndex !== placeholderIndexRef.current) {
        setPlaceholderIndex(newIndex);
      }
    },
    onDragEnd(event) {
      // Skip in read-only mode
      if (isReadOnly) {
        setIsDraggingNewTool(false);
        setPlaceholderIndex(null);
        draggedStepTypeRef.current = null;
        return;
      }
      // =======================================================================
      // PLACEHOLDER-DRIVEN INSERTION - Single source of truth
      // =======================================================================
      // The placeholderIndex determines EXACTLY where the new node will be
      // inserted. This ensures visual preview matches final position.
      // 
      // CONTRACT:
      // - placeholderIndex is the insert position (0-based index)
      // - If placeholderIndex === workflowSteps.length, append at end
      // - We call onAddStep here, AutomationBuilder skips TOOL handling
      // =======================================================================
      if (isDraggingNewToolRef.current && onAddStep) {
        const stepType = draggedStepTypeRef.current;
        const insertAt = placeholderIndexRef.current;

        if (stepType && insertAt !== null) {
          // Call onAddStep with the exact placeholder position
          // If at end of list, pass the length (append)
          onAddStep(stepType, insertAt);
        }
      }

      // Clean up state
      setIsDraggingNewTool(false);
      setPlaceholderIndex(null);
      draggedStepTypeRef.current = null;
    },
    onDragCancel() {
      // Clean up placeholder on cancel - no insertion
      setIsDraggingNewTool(false);
      setPlaceholderIndex(null);
      draggedStepTypeRef.current = null;
    },
  });

  // =============================================================================
  // DROPPABLE ZONE - For sidebar tool drops
  // =============================================================================
  // This makes the entire canvas a valid drop target for tools from ToolsBar.
  // When a tool is dropped here (not on a specific node), it appends to the end.
  // =============================================================================
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: 'workflow-canvas',
  });

  // =============================================================================
  // SORTABLE CONTEXT ITEMS - Including placeholder for new node preview
  // =============================================================================
  // Visual-only placeholder. Does not affect drop logic.
  // 
  // When dragging a new tool, we insert a placeholder step into the items array.
  // This allows @dnd-kit to compute displacement for all nodes around it.
  // The placeholder is temporary and NEVER persisted.
  // =============================================================================
  const stepsWithPlaceholder = useMemo(() => {
    // If not dragging a new tool, use original steps
    if (!isDraggingNewTool || placeholderIndex === null) {
      return workflowSteps;
    }

    // Insert placeholder at computed position
    // Visual-only. Does not affect drop logic.
    const placeholder = {
      id: PLACEHOLDER_ID,
      type: 'placeholder',
      title: '',
      subtitle: '',
      icon: null,
    };

    const result = [...workflowSteps];
    result.splice(placeholderIndex, 0, placeholder);
    return result;
  }, [workflowSteps, isDraggingNewTool, placeholderIndex]);

  // Extract step IDs for SortableContext (includes placeholder when active)
  const stepIds = stepsWithPlaceholder.map(step => step.id);

  // ========== MOBILE LAYOUT ==========
  if (isMobile) {
    return (
      <div
        ref={setDroppableRef}
        className={`flex-1 overflow-y-auto p-4 bg-stone-50 ${isOver ? 'bg-blue-50/30' : ''}`}
      >
        <div className="pb-32">
          <div className="flex items-center justify-center gap-2 mb-4 text-stone-400 text-[10px] uppercase tracking-widest font-semibold">
            Workflow Sequence
          </div>

          {/* 
            SORTABLE CONTEXT - Enables @dnd-kit sorting with live displacement
            
            Visual-only displacement. Does not affect drop logic.
            
            - items: Array of step IDs (includes placeholder when dragging new tool)
            - strategy: verticalListSortingStrategy for vertical list behavior
            - @dnd-kit automatically computes transforms for displaced items
            - WorkflowNode receives these transforms via useSortable()
            - Non-dragged nodes shift smoothly to preview drop position
            - Placeholder enables preview for NEW nodes, not just reordering
          */}
          <SortableContext
            items={stepIds}
            strategy={verticalListSortingStrategy}
          >
            <div ref={nodeListRef} className="flex flex-col shadow-card rounded-xl">
              {stepsWithPlaceholder.map((step, index) => (
                <WorkflowNode
                  key={step.id}
                  step={step}
                  isFirst={index === 0}
                  isLast={index === stepsWithPlaceholder.length - 1}
                  isSelected={selectedNode === step.id}
                  onClick={() => onNodeClick(step.type, step.id)}
                  forms={forms}
                  automationStatus={automationStatus}
                  hasSteps={workflowSteps.length > 1}
                  onRemoveStep={step.type !== 'trigger' ? onRemoveStep : undefined}
                  isMobile={isMobile}
                  isReadOnly={isReadOnly}
                />
              ))}
            </div>
          </SortableContext>

          {/* 
            BOTTOM DROP ZONE - Visual drop zone hint
            Highlights when dragging over to indicate append position.
          */}
          {isReadOnly ? (
            <div className="mt-4 border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-stone-400 bg-stone-50/50 border-stone-200">
              <span className="text-xs font-medium text-center text-stone-400">Workflow is running — stop to edit</span>
            </div>
          ) : (
            <div
              className={`mt-4 border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-stone-400 bg-stone-50/50 transition-colors ${isOver
                ? 'border-blue-400 bg-blue-50'
                : 'border-stone-200'
                }`}
            >
              <Plus className="w-6 h-6 mb-2 text-stone-300" />
              <span className="text-xs font-medium text-center">Drag tools from top to add</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========== DESKTOP LAYOUT ==========
  return (
    <div
      ref={setDroppableRef}
      className={`flex-1 relative overflow-y-auto p-8 ${isOver ? 'bg-blue-50/20' : ''}`}
    >
      <div className="max-w-2xl mx-auto pb-40">
        <div className="flex items-center justify-center gap-2 mb-6 text-stone-400 text-xs uppercase tracking-widest font-semibold">
          Workflow Sequence
        </div>

        {/* 
          SORTABLE CONTEXT - Enables @dnd-kit sorting with live displacement
          
          Visual-only displacement. Does not affect drop logic.
          
          - items: Array of step IDs (includes placeholder when dragging new tool)
          - strategy: verticalListSortingStrategy for vertical list behavior
          - @dnd-kit automatically computes transforms for displaced items
          - WorkflowNode receives these transforms via useSortable()
          - Non-dragged nodes shift smoothly to preview drop position
          - Placeholder enables preview for NEW nodes, not just reordering
        */}
        <SortableContext
          items={stepIds}
          strategy={verticalListSortingStrategy}
        >
          <div ref={nodeListRef} className="flex flex-col shadow-card rounded-xl">
            {stepsWithPlaceholder.map((step, index) => (
              <WorkflowNode
                key={step.id}
                step={step}
                isFirst={index === 0}
                isLast={index === stepsWithPlaceholder.length - 1}
                isSelected={selectedNode === step.id}
                onClick={() => onNodeClick(step.type, step.id)}
                forms={forms}
                automationStatus={automationStatus}
                hasSteps={workflowSteps.length > 1}
                onRemoveStep={step.type !== 'trigger' ? onRemoveStep : undefined}
                isMobile={isMobile}
                isReadOnly={isReadOnly}
              />
            ))}
          </div>
        </SortableContext>

        {/* 
          BOTTOM DROP ZONE - Visual drop zone hint
          Highlights when dragging over to indicate append position.
        */}
        {isReadOnly ? (
          <div className="mt-4 border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-stone-400 border-stone-200">
            <span className="text-xs font-medium">Workflow is running — stop to edit</span>
          </div>
        ) : (
          <div
            className={`mt-4 border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-stone-400 transition-colors ${isOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50/50'
              }`}
          >
            <Plus className="w-6 h-6 mb-2 text-stone-300" />
            <span className="text-xs font-medium">Drag actions here to continue workflow</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowStack;
